import isArray from 'lodash/isArray'

import type { Dayjs } from 'configuration/dayjs'
import { DATA_SOURCES } from 'configuration/widgets'
import { WidgetDataSourceKey } from 'const/dataSources'
import { FilterValue } from 'types'
import { DateGrain } from 'types/dataSources'

export class DateRangeWithLagUtil {
    static includePreset = [
        'last_7_days',
        'last_14_days',
        'last_28_days',
        'last_30_days',
        'last_month',
        'last_3_months',
        'last_6_months',
        'last_12_months',
        'last_quarter',
        'quarter_to_date',
        'year_to_date',
        'this_year',
        'last_60_days',
        'last_90_days',
        'last_180_days',
        'last_365_days',
    ]

    static getDatasourceWithBiggestLag(
        dataSources: WidgetDataSourceKey[] | undefined
    ): { name: WidgetDataSourceKey; lag: number } | null {
        if (!dataSources) {
            return null
        }

        // `lag` is a getter on some data sources, so read it once per source.
        const dsWithLag = dataSources
            .map((ds) => ({ name: ds, lag: DATA_SOURCES[ds]?.lag || 0 }))
            .filter(({ lag }) => lag)

        if (dsWithLag.length === 0) {
            return { name: '', lag: 0 }
        }

        return dsWithLag.reduce((prev, curr) =>
            prev.lag > curr.lag ? prev : curr
        )
    }

    /**
     * The grain every one of these data sources shares, or null if they don't.
     *
     * Note the asymmetry with `getDatasourceWithBiggestLag` above, and keep it:
     * lag **maxes** because it is scalar and monotone — the largest lag is safe
     * for everything on the tab. Grain must be **unanimous**, because no window
     * satisfies two grains at once. A tab holding one weekly source and one daily
     * source therefore keeps the generic picker, and the divergence is stated per
     * widget instead of being resolved here.
     */
    static getSharedDateGrain(
        dataSources: WidgetDataSourceKey[] | undefined
    ): DateGrain | null {
        if (!dataSources?.length) {
            return null
        }

        const grains = dataSources.map((ds) => DATA_SOURCES[ds]?.dateGrain)
        const [first] = grains
        if (!first) {
            return null
        }

        return grains.every((grain) => grain?.unit === first.unit)
            ? first
            : null
    }

    /**
     * True when some — but not all — of these data sources declare a grain, so
     * one picked range legitimately resolves to a different window per widget.
     */
    static hasMixedDateGrain(
        dataSources: WidgetDataSourceKey[] | undefined
    ): boolean {
        return (
            !!dataSources?.some((ds) => DATA_SOURCES[ds]?.dateGrain) &&
            !this.getSharedDateGrain(dataSources)
        )
    }

    static shouldOverrideRange(
        selectedRange: FilterValue | Dayjs[] | string[],
        dataSource: WidgetDataSourceKey | null | undefined
    ): boolean {
        if (isArray(selectedRange) || !dataSource) {
            return false
        }

        const isDsWithLag = !!DATA_SOURCES[dataSource]?.lag

        return isDsWithLag && this.includePreset.includes(selectedRange)
    }
}
